import { normalizeQueryPageSize, SHIPPER_NAMES_MAX_SIZE } from './shipperNamesSize'
import { InfiniteData, UseInfiniteQueryResult, UseQueryResult, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import {
  getShipperListV2,
  PAGE_SIZE_ON_SHIPPER,
  createMWPShipper,
  createMLTShipper,
  getShipperNames,
  updateMWPShipper,
  updateMLTShipper,
  createSalesPlatform,
  updateSalesPlatform,
  deleteSalesPlatform,
  updateMFSShipper,
  updateMCDShipper,
  createMCDShipper,
  searchSignupShippers,
  getSignupDepartments,
  getShipperDropdown,
} from '@/services/shipper/apis'
import {
  CreateSalesPlatformReq,
  DeleteSalesPlatformReq,
  DepartmentSearchItem,
  GetShipperListV2Req,
  GetShipperNamesReq,
  ShipperItem,
  ShipperSearchItem,
  UpdateSalesPlatformReq,
} from './types'

const PATH = ['common', 'shipper']

export const GET_SHIPPER_LIST_V2 = ['GET_SHIPPER_LIST_V2']

export const GET_SHIPPER_NAMES = ['GET_SHIPPER_NAMES']

// LOGISPRD-8888/9458 — Okta 회원가입(비로그인) 화주 검색. 응답({shipperCode,shipperName})을 모달 호환 형태(ShipperSearchItem)로 매핑.
export const useSearchSignupShippers = (keyword = '') => {
  const { data, ...rest } = useQuery({
    queryKey: [...PATH, 'SIGNUP_SHIPPER_SEARCH', keyword],
    queryFn: () => searchSignupShippers(keyword.trim()),
    enabled: false,
    // 키워드 변경 시 새 결과 도착 전까지 이전 결과 유지 → 자동검색 중 깜빡임 방지
    placeholderData: (prev) => prev,
  })

  // LOGISPRD-9458 — 부분일치(like) 검색 미지원. 비로그인 사용자의 화주 목록 열람 방지를 위해
  // 입력값과 정확히 일치하는 화주만 노출(클라이언트 가드, 대소문자·공백 무시). BE 도 exact 로 제한됨.
  const normalized = keyword.trim().toLowerCase()
  const list = (data?.data?.items ?? [])
    .filter((s) => s.shipperName.trim().toLowerCase() === normalized)
    .map((s): ShipperSearchItem => ({ shipper_code: s.shipperCode, shipper_name: s.shipperName }))

  return { ...rest, data: { list } }
}

// Spec: docs/spec/auth/SPEC-auth-signup-public-api-reduction-A.yaml#signup-department-list-carries-name-only
export const useGetSignupDepartments = (shipperCode = '') => {
  const { data, ...rest } = useQuery({
    queryKey: [...PATH, 'SIGNUP_DEPARTMENTS', shipperCode],
    queryFn: () => getSignupDepartments(shipperCode.trim()),
    enabled: false,
  })

  const list = (data?.data?.items ?? []).map((d): DepartmentSearchItem => ({ DEPT_NM: d.deptName }))

  return { ...rest, data: { list } }
}

export const useGetShipperListV2 = (
  params: ApiPaginationReq<GetShipperListV2Req>,
): Omit<UseInfiniteQueryResult<InfiniteData<ApiPaginationV2<ShipperItem>>, Error>, 'data'> & {
  data: { list: ShipperItem[]; totalCount: number; totalPages: number; currentPage: number }
} => {
  const { shipperNames = [], logisticsServiceTypes = [], page = 0 } = params
  const { data, ...rest } = useInfiniteQuery({
    queryKey: [...GET_SHIPPER_LIST_V2, shipperNames, logisticsServiceTypes, page],
    queryFn: () => getShipperListV2({ shipperNames, logisticsServiceTypes, page, size: PAGE_SIZE_ON_SHIPPER }, 'MA14'),
    initialPageParam: { page: 0 },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.data.pageInfo.page ?? 0
      const totalPages = lastPage?.data?.pageInfo?.totalPages ?? 1

      if (currentPage >= totalPages) {
        return undefined
      }
      return { page: currentPage + 1 }
    },
    enabled: false,
  })

  return {
    ...rest,
    data: {
      list: data?.pages.flatMap((page) => page.data.items || []) ?? [],
      totalCount: data?.pages[0]?.data?.pageInfo?.totalCount ?? 0,
      totalPages: data?.pages[0]?.data?.pageInfo?.totalPages ?? 0,
      currentPage: data?.pages[0]?.data?.pageInfo?.page ?? 0,
    },
  }
}

export const GET_SHIPPER_OPTIONS = [...PATH, 'GET_SHIPPER_OPTIONS']

/**
 * 화면 필터의 화주유형·화주명 옵션 생성용 — `useGetShipperListV2` 는 페이지네이션(useInfiniteQuery)
 * 이라 "화주 전체를 한 번에" 라는 이 용도와 안 맞아 별도로 둔다(size 10000 단일 조회, RGR04 등
 * 화주 옵션만 필요한 필터가 공유해 쓸 수 있다).
 */
export const useGetShipperOptions = (logisticsServiceTypes: string[] = []): UseQueryResult<ShipperItem[], Error> =>
  useQuery({
    queryKey: [...GET_SHIPPER_OPTIONS, logisticsServiceTypes],
    queryFn: async () => {
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes, page: 0, size: 10000 })
      return res?.data?.items ?? []
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

export const GET_SHIPPER_DROPDOWN = [...PATH, 'GET_SHIPPER_DROPDOWN']

/**
 * 로그인 사용자의 허용 화주코드 (LOGISPRD-12495) — 출고일정관리 화주 선택지를 좁히는 데 쓴다.
 * 실패 알림은 화면이 대신 판단한다(고정 선택지로 내려가고 판정은 서버 가드) — 전역 MAlert 를 끈다.
 */
export const useGetAllowedShipperCodes = (): UseQueryResult<string[], Error> =>
  useQuery({
    queryKey: GET_SHIPPER_DROPDOWN,
    queryFn: async () => {
      const res = await getShipperDropdown()
      return res?.data?.items?.map(({ shipperCode }) => shipperCode) ?? []
    },
    staleTime: API_COMMON_CACHE_TIME,
    meta: { noErrorMessage: true },
  })

export const useGetShipperNames = (
  params: GetShipperNamesReq & { returnScreens?: boolean },
): Omit<UseQueryResult<string[], Error>, 'data'> & { data: string[] | undefined } => {
  /** MEMO: api 자체는 pagination되도록 만들어졌는데 현재는 검색을 클라이언트에서 해야해서 useQuery사용하고 size를 10,000으로 요청하기로 함. */
  const { logisticsServiceTypes, size: requestedSize, returnScreens } = params
  const size = normalizeQueryPageSize(requestedSize, SHIPPER_NAMES_MAX_SIZE)
  const { data, ...rest } = useQuery({
    // 정규화 전 requestedSize 를 키에 쓰면 size 99999 와 100001 이 다른 캐시 엔트리가 되는데 요청은 같다 — 캐시가 안 맞는다.
    queryKey: [...GET_SHIPPER_NAMES, logisticsServiceTypes, size, returnScreens ?? false],
    queryFn: () => getShipperNames({ logisticsServiceTypes, size, ...(returnScreens ? { returnScreens } : {}) }),
    enabled: !!logisticsServiceTypes && logisticsServiceTypes.length > 0,
    select: (data) => data?.data?.items || [],
  })

  return {
    ...rest,
    data,
  }
}

// Create mutations
export const useCreateMWPShipper = () => {
  return useMutation({
    mutationFn: createMWPShipper,
  })
}

export const useCreateMLTShipper = () => {
  return useMutation({
    mutationFn: createMLTShipper,
  })
}

export const useCreateMCDShipper = () => {
  return useMutation({
    mutationFn: createMCDShipper,
  })
}

export const useUpdateMWPShipper = () => {
  return useMutation({
    mutationFn: updateMWPShipper,
  })
}

export const useUpdateMLTShipper = () => {
  return useMutation({
    mutationFn: updateMLTShipper,
  })
}

export const useUpdateMFSShipper = () => {
  return useMutation({
    mutationFn: updateMFSShipper,
  })
}

export const useUpdateMCDShipper = () => {
  return useMutation({
    mutationFn: updateMCDShipper,
  })
}

export const useCreateSalesPlatform = (shipperCode: string) => {
  return useMutation({
    mutationFn: (params: CreateSalesPlatformReq) => createSalesPlatform({ shipperCode, ...params }),
  })
}

export const useUpdateSalesPlatform = (shipperCode: string) => {
  return useMutation({
    mutationFn: (params: UpdateSalesPlatformReq) => updateSalesPlatform({ shipperCode, ...params }),
  })
}

export const useDeleteSalesPlatform = (shipperCode: string) => {
  return useMutation({
    mutationFn: (params: DeleteSalesPlatformReq) => deleteSalesPlatform({ shipperCode, ...params }),
  })
}
